import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Flame, Lightbulb, Coins, Grid3X3,
  Zap, Trophy, Star, Crown, Gift, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  flame: Flame,
  lightbulb: Lightbulb,
  coins: Coins,
  grid: Grid3X3,
  zap: Zap,
  trophy: Trophy,
  star: Star,
  crown: Crown,
};

const MILESTONE_COLORS = {
  iron_brain: { bg: 'from-orange-400 to-red-500', light: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', accent: '#f97316' },
  riddle_master: { bg: 'from-violet-400 to-purple-600', light: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', accent: '#8b5cf6' },
  coin_millionaire: { bg: 'from-amber-400 to-yellow-500', light: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', accent: '#f59e0b' },
  set_collector: { bg: 'from-sky-400 to-blue-500', light: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', accent: '#0ea5e9' },
  dragon_tamer: { bg: 'from-yellow-400 to-orange-500', light: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', accent: '#eab308' },
  ultimate_collector: { bg: 'from-emerald-400 to-teal-500', light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', accent: '#10b981' },
  uncommon_collector: { bg: 'from-pink-400 to-rose-500', light: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', accent: '#ec4899' },
  brain_champion: { bg: 'from-indigo-400 to-blue-600', light: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', accent: '#6366f1' },
};

const RealPrizesPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  useEffect(() => {
    fetchMilestones();
  }, []);

  const fetchMilestones = async () => {
    try {
      const response = await api.get('/milestones');
      setMilestones(response.data.milestones || []);
    } catch (error) {
      console.error('Failed to fetch milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (milestoneId) => {
    setClaimingId(milestoneId);
    try {
      const response = await api.post(`/milestones/${milestoneId}/claim`);
      toast.success(response.data.message);
      fetchMilestones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim reward');
    } finally {
      setClaimingId(null);
    }
  };

  const completedCount = milestones.filter(m => m.completed).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce-soft mb-4">
            <Trophy className="w-16 h-16 text-amber-400 mx-auto" />
          </div>
          <p className="text-xl text-slate-600">Loading your prizes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-rose-50" data-testid="real-prizes-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-amber-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6 text-slate-600" />
          </button>
          <h1 className="text-xl font-bold text-slate-900 font-fredoka ml-3">Real Prizes</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Hero Banner */}
        <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-amber-200 text-center relative overflow-hidden">
          <div className="absolute top-3 left-6 text-2xl opacity-30 animate-pulse-soft">&#10024;</div>
          <div className="absolute top-5 right-8 text-xl opacity-20 animate-pulse">&#127775;</div>
          <div className="absolute bottom-3 left-1/4 text-lg opacity-20">&#11088;</div>
          <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 font-fredoka mb-2">
            Earn Real Prizes!
          </h2>
          <p className="text-slate-600 max-w-md mx-auto">
            Complete amazing goals and earn real-world rewards. Keep training your brain!
          </p>
          <div className="mt-4 inline-flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full">
            <Star className="w-4 h-4 text-amber-600" />
            <span className="font-bold text-amber-700 text-sm">
              {completedCount} of {milestones.length} completed
            </span>
          </div>
        </div>

        {/* Milestones Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {milestones.map((m) => {
            const colors = MILESTONE_COLORS[m.id] || MILESTONE_COLORS.iron_brain;
            const IconComp = ICON_MAP[m.icon] || Gift;
            const isComplete = m.completed;
            const isClaimed = m.claimed;
            const isFulfilled = m.claim_status === 'fulfilled';

            return (
              <button
                key={m.id}
                onClick={() => setSelectedMilestone(m)}
                className={`relative ${colors.light} ${colors.border} border-2 rounded-3xl p-4 text-center flex flex-col items-center group transition-all hover:scale-[1.03] active:scale-95`}
                data-testid={`milestone-${m.id}`}
              >
                {/* Completion badge */}
                {isFulfilled ? (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center shadow-sm" data-testid={`milestone-fulfilled-badge-${m.id}`}>
                    <Gift className="w-3.5 h-3.5 text-white" />
                  </div>
                ) : isClaimed ? (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center shadow-sm" data-testid={`milestone-claimed-badge-${m.id}`}>
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                ) : null}

                {/* Icon */}
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.bg} flex items-center justify-center shadow-lg mb-3 group-hover:shadow-xl transition-shadow`}>
                  <IconComp className="w-7 h-7 text-white" />
                </div>

                {/* Name */}
                <h3 className="text-sm font-bold text-slate-900 font-fredoka mb-1 leading-tight">
                  {m.name}
                </h3>

                {/* Progress ring / status */}
                {isClaimed ? (
                  <div className={`mt-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    isFulfilled 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {isFulfilled ? 'Prize Delivered' : 'Claimed'}
                  </div>
                ) : (
                  <div className="w-full mt-2">
                    <div className="w-full h-2 bg-slate-200/70 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 bg-gradient-to-r ${colors.bg}`}
                        style={{ width: `${m.percent}%` }}
                      />
                    </div>
                    <p className={`text-[11px] font-bold mt-1 ${colors.text}`}>{m.percent}%</p>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Reward Info */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-6">
          <h3 className="font-bold text-slate-900 font-fredoka mb-3 flex items-center gap-2">
            <Gift className="w-5 h-5 text-purple-500" />
            How It Works
          </h3>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <span className="text-lg">&#127942;</span>
              <span>Complete goals by doing your daily Brain Gym, collecting pets, and solving riddles!</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-lg">&#127873;</span>
              <span>Once a goal is done, tap it to claim your real-world reward!</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-lg">&#128075;</span>
              <span>Show the claimed badge to your instructor to receive your prize!</span>
            </li>
          </ul>
        </div>

        {/* Selected Milestone Detail Modal */}
        {selectedMilestone && (() => {
          const m = selectedMilestone;
          const colors = MILESTONE_COLORS[m.id] || MILESTONE_COLORS.iron_brain;
          const IconComp = ICON_MAP[m.icon] || Gift;
          const isClaimed = m.claimed;
          const isFulfilled = m.claim_status === 'fulfilled';

          return (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setSelectedMilestone(null)}
            >
              <div
                className="bg-white rounded-3xl p-6 w-full max-w-sm animate-pop text-center"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Icon */}
                <div
                  className={`w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${colors.bg} flex items-center justify-center shadow-xl`}
                >
                  <IconComp className="w-10 h-10 text-white" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 font-fredoka mb-1">
                  {m.name}
                </h3>
                <p className="text-slate-600 text-sm mb-4">{m.description}</p>

                {/* Progress */}
                {!isClaimed && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1 px-1">
                      <span className="text-xs font-semibold text-slate-500">{m.current} / {m.target}</span>
                      <span className={`text-xs font-bold ${colors.text}`}>{m.percent}%</span>
                    </div>
                    <div className="w-full h-3 bg-slate-200/70 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 bg-gradient-to-r ${colors.bg}`}
                        style={{ width: `${m.percent}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Reward */}
                <div className="flex items-center justify-center gap-2 mb-5 bg-amber-50 rounded-xl py-2 px-4">
                  <Gift className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-700 font-medium">
                    {m.reward_label || 'Real prize — to be announced!'}
                  </span>
                </div>

                {/* Action */}
                {m.completed && !isClaimed ? (
                  <button
                    onClick={() => { handleClaim(m.id); setSelectedMilestone(null); }}
                    disabled={claimingId === m.id}
                    className={`w-full py-3 rounded-2xl font-bold text-white bg-gradient-to-r ${colors.bg} shadow-lg hover:shadow-xl transition-all disabled:opacity-60`}
                    data-testid={`claim-btn-${m.id}`}
                  >
                    {claimingId === m.id ? 'Claiming...' : 'Claim Prize!'}
                  </button>
                ) : isFulfilled ? (
                  <div className="w-full py-3 rounded-2xl font-bold text-amber-700 bg-amber-100 text-center flex items-center justify-center gap-2">
                    <Gift className="w-4 h-4" /> Prize Delivered
                  </div>
                ) : isClaimed ? (
                  <div className="w-full py-3 rounded-2xl font-bold text-green-700 bg-green-100 text-center">
                    Claimed — Pending Delivery
                  </div>
                ) : (
                  <button
                    onClick={() => setSelectedMilestone(null)}
                    className="w-full py-3 bg-slate-100 rounded-2xl font-bold text-slate-700"
                  >
                    Keep Going!
                  </button>
                )}
              </div>
            </div>
          );
        })()}
      </main>
    </div>
  );
};

export default RealPrizesPage;
